import React, { useEffect, useMemo, useState } from 'react';
import { formatDate, useAdmin, useDebounced } from '../context';
import { Icon } from '../icons';
import { StaffAudit, StaffMemberView, StaffState } from '../types';
import { Empty, Segmented } from '../ui';
import { AUDIT_LABEL, AuditGroup, auditGroupOf } from './helpers';

export const AuditTab: React.FC<{ staff: StaffState; members: StaffMemberView[]; audit: StaffAudit | null }> = ({ staff, members, audit }) => {
  const { send } = useAdmin();
  const [group, setGroup] = useState<AuditGroup>('all');
  const [who, setWho] = useState('');
  const [search, setSearch] = useState('');
  const dSearch = useDebounced(search, 120);

  // A chosen person: ask the server for their whole history, not only the last 50 entries of the register
  useEffect(() => {
    if (who) send({ action: 'adminStaffAuditRequest', discordId: who });
  }, [who, send]);

  const person = who ? members.find((m) => m.discordId === who) || null : null;

  const entries = useMemo(() => {
    let list = staff.audit;
    if (who) {
      if (audit && audit.discordId === who) list = audit.entries;
      else {
        const keys = [who, person ? person.name : ''].filter(Boolean).map((k) => k.toLowerCase());
        list = list.filter((e) => keys.indexOf(String(e.target).toLowerCase()) !== -1);
      }
    }
    const q = dSearch.trim().toLowerCase();
    return list
      .filter((e) => group === 'all' || auditGroupOf(e) === group)
      .filter((e) => !q || [e.by, e.target, e.detail, AUDIT_LABEL[e.action] || e.action].join(' ').toLowerCase().indexOf(q) !== -1)
      .slice()
      .sort((a, b) => b.at - a.at);
  }, [staff.audit, audit, who, person, group, dSearch]);

  return (
    <div className="admin-panel__audittab">
      <div className="admin-panel__toolbar">
        <div className="admin-panel__searchfield admin-panel__input--grow">
          <Icon name="search" size={15} className="admin-panel__searchfield-icon" />
          <input className="admin-panel__input admin-panel__input--block" type="search" placeholder="Rechercher dans le journal" aria-label="Rechercher dans le journal" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <label className="admin-panel__select-wrap">
          <span className="admin-panel__select-label">Personne</span>
          <select className="admin-panel__select" value={who} onChange={(e) => setWho(e.target.value)}>
            <option value="">Tout le registre</option>
            {members.map((m) => (
              <option key={m.discordId} value={m.discordId}>{m.name || m.discordId}</option>
            ))}
          </select>
        </label>
        <Segmented<AuditGroup>
          label="Filtrer par type"
          value={group}
          onChange={setGroup}
          options={[
            { id: 'all', label: 'Tout' },
            { id: 'member', label: 'Membres' },
            { id: 'role', label: 'Rôles' },
            { id: 'bulk', label: 'Groupées' },
          ]}
        />
      </div>
      <p className="admin-panel__hint">{who ? 'Historique complet de cette personne.' : 'Les 50 dernières modifications du registre, les plus récentes en premier.'}</p>

      {entries.length === 0 ? (
        <Empty title="Aucune entrée" text="Rien ne correspond à ce filtre. Les modifications du registre apparaissent ici dès qu'elles ont lieu." />
      ) : (
        <ul className="admin-panel__timeline">
          {entries.map((e, i) => (
            <li key={e.at + ':' + i} className="admin-panel__tl-item">
              <span className={'admin-panel__tl-dot admin-panel__tl-dot--' + auditGroupOf(e)} aria-hidden="true" />
              <div className="admin-panel__tl-main">
                <div className="admin-panel__tl-title">
                  {AUDIT_LABEL[e.action] || e.action}
                  {e.target ? <span className="admin-panel__tl-target">{e.target}</span> : null}
                </div>
                <div className="admin-panel__tl-detail">{e.detail}</div>
                <div className="admin-panel__tl-meta">{e.by ? `Par ${e.by}, ` : ''}{formatDate(e.at)}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
